import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Confirms replacing an existing file before that one file's upload
 * proceeds (FR-009). Purely presentational — `UploadControls` owns the
 * collision queue and decides what "confirm"/"cancel" do (start the upload,
 * or skip it and move to the next queued collision), so this dialog never
 * calls the upload mutation itself.
 */
export function OverwriteConfirmDialog({
  fileName,
  open,
  onConfirm,
  onCancel,
  onKeepBoth,
  where = 'in this folder',
}: {
  fileName: string;
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * A third way out: keep both files, the new one under a numbered name. Only
   * offered where the server names the copy itself — a task's attachments
   * (spec 120 FR-007) — so the Files page keeps its two buttons.
   */
  onKeepBoth?: () => void;
  /** Where the clash is, as the sentence says it. */
  where?: string;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Replace &ldquo;{fileName}&rdquo;?</DialogTitle>
          <DialogDescription>
            A file named &ldquo;{fileName}&rdquo; already exists {where}.
            Replacing it overwrites its content. This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Skip this file
          </Button>
          {onKeepBoth ? (
            <Button variant="outline" onClick={onKeepBoth}>
              Keep both
            </Button>
          ) : null}
          <Button variant="destructive" onClick={onConfirm}>
            Replace
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
